import type { RowData } from '@tanstack/react-table';
import {
  Children,
  cloneElement,
  isValidElement,
  type ReactElement,
} from 'react';

import type { DataTableColumnDef } from '../../public.api.js';
import type { CollectedColumnActions } from './ColumnActions.js';

/**
 * Collects and resolves column actions for a specific column from the provided options.
 * Filters by column id, then resolves function-based actions using the consumerColumnDefMap.
 *
 * @param columnId - the id of the column to get actions for.
 * @param columnActions - the collection of all column actions.
 * @param consumerColumnDefMap - map from column id to the consumer-provided column definition.
 * @returns an array of resolved ReactElement column actions for the specified column.
 * @internal
 */
export function getColumnActionsElements<TData extends RowData>(
  columnId: string,
  columnActions: CollectedColumnActions<TData>,
  consumerColumnDefMap: Map<string, DataTableColumnDef<TData>> | undefined,
): ReactElement[] {
  return columnActions.reduce<ReactElement[]>((actions, { column, action }) => {
    if (column === undefined || column === columnId) {
      // If the entry is a react element, we can directly return it
      if (isValidElement(action)) {
        actions.push(action);
        // If it is a function, we will need to resolve the closure first
      } else if (typeof action === 'function') {
        const columnDef = consumerColumnDefMap?.get(columnId);
        if (columnDef !== undefined) {
          actions.push(action(columnDef));
        }
      }
    }
    return actions;
  }, []);
}

/**
 * Array of invalid built-in column action function names for chart columns.
 * Valid built-in actions are CopyItem, HideColumn, ColumnOrder, and ColumnPinning.
 *
 * Note: these must match the actual function names of the action components
 * defined in `UserActionsMenu.tsx` (e.g. `LineWrapAction`, `ColumnFontStyleAction`).
 */
const INVALID_CHART_BUILTIN_COLUMN_ACTIONS = new Set([
  'LineWrapAction',
  'ColumnFontStyleAction',
]);

/**
 * Iterates over all column actions and filters out invalid actions for chart columns.
 * Invalid actions include specific built-in actions like "LineWrap" and "ColumnFontStyle".
 * All other actions (valid built-ins and custom actions) are allowed.
 *
 * @param columnActions - the column actions to filter
 * @returns a new array containing all valid column actions for chart columns.
 * @internal
 */
export function filterActionsForChartColumn(
  columnActions: ReactElement[],
): ReactElement[] {
  const menuElements = Children.toArray(columnActions);

  return menuElements
    .map((menuElement) => {
      if (!isValidElement(menuElement)) {
        return null;
      }

      // Filter out invalid built-in column actions for chart columns
      const isInvalidColumnAction =
        typeof menuElement.type !== 'string' &&
        INVALID_CHART_BUILTIN_COLUMN_ACTIONS.has(menuElement.type.name);

      // filter out the invalid column action
      if (isInvalidColumnAction) {
        return null;
      }

      // Recursively filter the elements in the column actions menu
      const filteredElements = (
        menuElement as ReactElement<{ children: ReactElement[] }>
      ).props?.children
        ? filterActionsForChartColumn(
            (menuElement as ReactElement<{ children: ReactElement[] }>).props
              .children,
          )
        : null;

      // If the element has children, return it with filtered children
      if (filteredElements && filteredElements.length > 0) {
        return cloneElement(
          menuElement as ReactElement<{ children: ReactElement[] }>,
          {
            ...(menuElement as ReactElement<{ children: ReactElement[] }>)
              .props,
            children: filteredElements,
          },
        );
      }
      // otherwise return the element as is
      return menuElement;
    })
    .filter((columnAction) => columnAction !== null);
}
